import { listMatchLeaderboards, listStageLeaderboards, listBullseyeMatchLeaderboards, listCompetitionResults, listCompetitionEntries } from "../../lib/leaderboardApi";

export async function loadDirectorLiveScores(match) {
  const [matchRows, stageRows, results, entries] = await Promise.all([
    match.family === "arc" ? listMatchLeaderboards({ matchId: match.matchId, requireComplete: true }) : listBullseyeMatchLeaderboards({ matchId: match.matchId, requireComplete: true }),
    match.family === "arc" ? listStageLeaderboards({ matchId: match.matchId, requireComplete: true }) : Promise.resolve([]),
    listCompetitionResults(match.matchId, { auth: true }), listCompetitionEntries(match.matchId),
  ]);
  return { matchRows, stageRows, results, entries };
}

const number = value => value !== "" && value != null && Number.isFinite(Number(value)) ? Number(value) : null;
const timestamp = value => Number.isFinite(Date.parse(value)) ? Date.parse(value) : 0;
const isConfirmed = row => row.reviewStatus === "confirmed" || ["official", "confirmed", "final"].includes(row.status);
const bullHasScore = row => Number(row.shotsFired) > 0 || Number(row.totalScore ?? row.classificationScore) > 0 || Number(row.xCount) > 0 || Number(row.completedScoreUnits) > 0 || ["scored", "audited", "official", "confirmed", "final"].includes(row.status);

// Only explicit IDs connect scoring records to people. Names and emails never establish identity.
function identityFor(participants, entries) {
  const legacy = new Map(participants.filter(person => person.legacyCompetitorId).map(person => [person.legacyCompetitorId, person.participantId]));
  const byEntry = new Map(entries.filter(entry => entry.entryId && entry.participantId).map(entry => [entry.entryId, entry.participantId]));
  return row => {
    const participant = row.participantId || byEntry.get(row.canonicalEntryId || row.entryId) || legacy.get(row.competitorId);
    if (participant) return `participant:${participant}`;
    if (row.competitorId) return `legacy:${row.competitorId}`;
    if (row.canonicalEntryId || row.entryId) return `entry:${row.canonicalEntryId || row.entryId}`;
    return null;
  };
}

export function directorLiveScores(match, scores, participants = []) {
  if (!scores) return null;
  const { matchRows = [], stageRows = [], results = [], entries = [] } = scores;
  const personKey = identityFor(participants, entries);
  // Follow the existing standings source preference; don't sum legacy rows and their snapshots.
  const aggregates = matchRows.length ? matchRows : results.filter(row => row.discipline === match.family);
  const uploaded = match.family === "arc" ? [...aggregates, ...stageRows] : aggregates.filter(bullHasScore);
  const people = new Set(uploaded.map(personKey).filter(Boolean));
  const updates = uploaded.map(row => timestamp(row.updatedAt)).filter(Boolean);
  const base = { people: people.size, unknownIdentity: uploaded.some(row => !personKey(row)), hasRecords: uploaded.length > 0,
    lastScoreUpdate: updates.length ? new Date(Math.max(...updates)).toISOString() : null };
  if (match.family === "arc") {
    const stageId = row => String(row.stageId || row.matchStageId || "").replace(`${match.matchId}#`, "");
    const configured = (match.stages || []).map(stage => ({ id: stage.stageId || stage.id, label: stage.name || stage.stageName })).filter(stage => stage.id);
    const ids = [...new Set([...configured.map(stage => stage.id), ...(match.stageIds || []), ...stageRows.map(stageId).filter(Boolean)])];
    const stages = ids.map((id, index) => {
      const latest = new Map();
      stageRows.filter(row => stageId(row) === id).forEach((row, rowIndex) => {
        const key = row.canonicalEntryId || row.entryId || personKey(row) || row.sk || `unlinked:${rowIndex}`;
        const previous = latest.get(key);
        if (!previous || timestamp(row.updatedAt) >= timestamp(previous.updatedAt)) latest.set(key, row);
      });
      const records = [...latest.values()];
      return { id, label: configured.find(stage => stage.id === id)?.label || `Stage ${index + 1}`, received: records.length, confirmed: records.filter(isConfirmed).length };
    });
    return { ...base, stages, received: stages.reduce((sum, stage) => sum + stage.received, 0), confirmed: stages.reduce((sum, stage) => sum + stage.confirmed, 0),
      stagesWithUploads: stages.filter(stage => stage.received > 0).length, expectedStages: Math.max(ids.length, number(match.expectedStageCount) || 0) };
  }
  const groups = new Map();
  aggregates.forEach(row => (row.firearms || []).forEach(group => {
    if (!group.gunGroupId) return;
    if (!groups.has(group.gunGroupId)) groups.set(group.gunGroupId, { id: group.gunGroupId, label: group.firearmTitle || "Scoring group", people: new Set(), recorded: 0, expected: 0, unitsAvailable: true });
    const target = groups.get(group.gunGroupId);
    if (bullHasScore(group) && personKey(row)) target.people.add(personKey(row));
    const units = (group.courses || []).flatMap(course => course.scoreUnits || []);
    if (!units.length) target.unitsAvailable = false;
    target.recorded += units.filter(unit => ["scored", "audited", "official"].includes(unit.status)).length;
    target.expected += units.length;
  }));
  const unitsAvailable = aggregates.length > 0 && aggregates.every(row => number(row.totalScoreUnits) > 0 && number(row.completedScoreUnits) != null);
  return { ...base, groups: [...groups.values()].map(group => ({ ...group, people: group.people.size })),
    unitsAvailable, recorded: unitsAvailable ? aggregates.reduce((sum, row) => sum + Number(row.completedScoreUnits), 0) : null,
    expected: unitsAvailable ? aggregates.reduce((sum, row) => sum + Number(row.totalScoreUnits), 0) : null };
}
